import { Add, PortraitSharp, Settings, TextSnippet } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import ButtonActionsComponent from 'components/ButtonActionsComponent';
import ButtonComponent from 'components/ButtonComponent';
import DeviceEditionComponent from 'components/DeviceEditionComponent';
import TableComponent, { IColumnProps } from 'components/TableComponent';
import React, { useContext, useEffect, useState } from 'react';
import {
  useDeleteDeviceByIdAsync,
  useGetAllDvrByLicenseAsync,
  useGetAllDvrByLicenseCountAsync,
} from 'services/Devices/hooks/useMutation';
import { IDeviceProps, IDeviceSimpleProps } from 'services/Devices/types';
import { IPaginationParams } from 'services/types';
import DeviceDetailsByDiscriminator from '../_components/DeviceDetailsByDiscriminator';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import { AppContext } from '../../../context/AppContextProvider';
import BrandIcons from '../_components/BrandIcons';
import LogsModalComponent from '../../../components/LogsComponent';
import HeaderDashboard from 'pages/DashboardPageClient/_components/_ui/Header';
import IsOnlineComponent from 'components/IsOnlineComponent';
import { DEVICE_DVRS_ENDPOINT } from 'services/Devices';
import {
  useDeleteDvrDeviceAsync,
  useGetDvrDeviceByIdAsync,
} from 'services/Devices/DvrService/hooks/useMutation';
import { IDvrDeviceProps } from 'services/Devices/DvrService/types';
import { getStreamByEndpointAsync } from 'services/Events';
import DeviceConfigModal from '../_components/DeviceConfigModal';
import DeviceLogStream from '../_components/DeviceLogStream';

const DvrPage = () => {
  const { showSnackbar } = useContext(AppContext);
  const [isVisibleEditionModal, setIsVisibleEditionModal] = useState(false);
  const [selectedItemToEdit, setSelectedItemToEdit] = useState<IDvrDeviceProps>(
    {} as IDvrDeviceProps,
  );
  const [isLoadingDvrs, setIsLoadingDvrs] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({
    name: '',
  });
  const [devicePaginationParams, setDevicePaginationParams] =
    useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [isVisibleConfigModal, setIsVisibleConfigModal] =
    useState<boolean>(false);
  const [logModal, setLogModal] = useState<{
    isVisible: boolean;
    deviceId: number;
  }>({ isVisible: false, deviceId: 0 });
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);
  const [devicesCount, setDevicesCount] = useState<number>(0);
  const { mutateAsync: mutateGetDvrDeviceById } = useGetDvrDeviceByIdAsync();
  const {
    mutateAsync: mutateDeleteDeviceById,
    isLoading: isLoadingDeleteDeviceById,
  } = useDeleteDvrDeviceAsync();
  const [isVisibleDeleteItemModal, setIsVisibleDeleteItemModal] =
    useState<boolean>(false);
  const [selectedItemToDelete, setSelectedItemToDelete] =
    useState<IDeviceSimpleProps>();

  const columns: IColumnProps[] = [
    { label: 'Ref.', align: 'center', id: 'id', width: 0 },
    {
      label: 'Nome',
      id: 'id',
      render(value, rowIndex) {
        const searched = devices.find((v) => v.id == Number.parseInt(value));
        if (searched) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
              <IsOnlineComponent
                width={18}
                height={18}
                isOnline={Boolean(searched.isOnline)}
              />
              {searched.name}
            </Box>
          );
        }
        return <></>;
      },
    },
    {
      label: 'Modelo',
      id: 'model',
      render: (val: string) => {
        return <>{val !== '' ? val : 'Não Informado'}</>;
      },
    },
    {
      label: 'Marca',
      width: 0,
      align: 'center',
      id: 'brand',
      render(val: any) {
        return <BrandIcons brand={val} />;
      },
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: 0,
      render: (id) => (
        <ButtonActionsComponent
          isWithDefaultOptionsList
          actionOptions={actionOptions(Number(id))}
          handleDelete={() => handleDeleteItem(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const actionOptions = (id: number) => {
    return [
      {
        label: 'Configurações',
        icon: <Settings />,
        onClick: () => {
          setIsVisibleConfigModal(true);
          handleConfig(id);
        },
      },
      {
        label: 'Logs',
        icon: <TextSnippet />,
        onClick: () => {
          setLogModal({ isVisible: true, deviceId: id });
        },
      },
    ];
  };

  const brands: any[] = [{ label: 'Intelbras', value: 'INTELBRAS' }];

  const { mutateAsync: mutateGetAllDvrCount } =
    useGetAllDvrByLicenseCountAsync();

  const handleConfig = async (id: number) => {
    await mutateGetDvrDeviceById(id).then(setSelectedItemToEdit);
    setIsVisibleConfigModal(true);
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteItemModal(false);
    setSelectedItemToDelete(undefined);
  };

  const handleConfirmDeletion = () => {
    if (selectedItemToDelete?.id)
      mutateDeleteDeviceById(selectedItemToDelete?.id).then(() => {
        setDevicePaginationParams((oldStt) => ({ ...oldStt, pageNumber: 1 }));
        setIsVisibleDeleteItemModal(false);
        showSnackbar('DVR/NVR excluído com sucesso.', 'success');
        getDevices(devicePaginationParams);
      });
  };

  const handleDeleteItem = async (id: number) => {
    const device: any = devices.find((i) => i.id === id);
    setSelectedItemToDelete(device);
    setIsVisibleDeleteItemModal(true);
  };

  const handleEdit = async (id: number) => {
    await mutateGetDvrDeviceById(id).then(setSelectedItemToEdit);
    setIsVisibleEditionModal(true);
  };

  const additionalButton = (
    <ButtonComponent
      startIcon={<Add />}
      size="small"
      onClick={() => setIsVisibleEditionModal(true)}
    >
      Novo DVR/NVR/NVD
    </ButtonComponent>
  );
  const getDevices = (pagination: IPaginationParams) => {
    getStreamByEndpointAsync(
      DEVICE_DVRS_ENDPOINT,
      setDevices,
      devicePaginationParams,
      undefined,
      setIsLoadingDvrs,
      undefined,
      true,
      filter,
    );
    mutateGetAllDvrCount({ filter }).then(setDevicesCount);
  };

  useEffect(() => {
    getDevices(devicePaginationParams);
  }, [devicePaginationParams]);

  return (
    <>
      <HeaderDashboard></HeaderDashboard>
      <TableComponent
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography color={'success'}>
              {DeviceDetailsByDiscriminator('DvrEntity')?.icon}
            </Typography>
            <Typography fontSize={19}>DVR/NVR/NVD</Typography>
          </Box>
        }
        expandedRowContent={(row: any) => <DeviceLogStream deviceId={row.id} />}
        isExpandedRow
        columns={columns}
        onChangeMainFilter={(val) =>
          setFilter((oldSt: any) => ({ ...oldSt, name: val }))
        }
        totalCount={devicesCount}
        onChangePageOrRowsPerPage={(param) => {
          setDevicePaginationParams(param);
          getDevices(param);
        }}
        onSubmitMainFilter={() => getDevices(devicePaginationParams)}
        rows={devices}
        additionalTitleComponent={additionalButton}
      />

      <DeviceEditionComponent
        isOpen={isVisibleEditionModal}
        deviceToEdit={selectedItemToEdit}
        handleSaveForms={(_, reset) => {
          getDevices(devicePaginationParams);
          setIsVisibleEditionModal(false);
          setSelectedItemToEdit({} as IDeviceProps);
          reset();
        }}
        isDvr
        setDeviceToEdit={setSelectedItemToEdit}
        discriminator={selectedItemToEdit.discriminator}
        setIsOpen={setIsVisibleEditionModal}
        brands={brands}
        availableEvents={
          DeviceDetailsByDiscriminator(selectedItemToEdit?.discriminator)
            ?.availableEvents ?? []
        }
      />
      <DeviceConfigModal
        discriminator={selectedItemToEdit?.discriminator}
        isOpen={isVisibleConfigModal}
        selectedDevice={selectedItemToEdit}
        setSelectedDevice={setSelectedItemToEdit}
        setIsOpen={setIsVisibleConfigModal}
      />
      <LogsModalComponent
        deviceId={logModal.deviceId}
        isVisible={logModal.isVisible}
        setIsVisible={(state) =>
          setLogModal((oldStt) => ({ ...oldStt, isVisible: state }))
        }
      />

      <ConfirmDeleteModalComponent
        isLoading={isLoadingDeleteDeviceById}
        isVisibleDeleteDialog={isVisibleDeleteItemModal}
        setIsVisibleDeleteDialog={setIsVisibleDeleteItemModal}
        title={'Excluir DVR/NVR'}
        message={`Tem certeza que deseja excluir o DVR/NVR "${selectedItemToDelete?.name}"?`}
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
      />
    </>
  );
};

export default DvrPage;
